// react-magnetic-di - temporary comment, for auto dependency injection in tests
import { useCallback, useEffect, useRef } from 'react';

import noop from 'lodash/noop';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireOperationalEvent } from '@atlassian/ui-modifications-analytics';

import type { ForgeExtension } from '../../../../../../common/types/apps';
import { useViewType } from '../../../../../../controllers/issue-adjustments/hooks/use-view-type';

type Props = {
	forgeExtension: ForgeExtension;
};

export const IFRAME_TIMEOUT_DURATION_MS = 12000;

export const useIframeTimeout = ({
	forgeExtension,
}: Props): {
	setLoadingComplete: () => void;
} => {
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const callbackRef = useRef(noop);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const viewType = useViewType();

	const onTimeout = useCallback(() => {
		const { id } = forgeExtension;

		fireOperationalEvent(createAnalyticsEvent, {
			action: 'failed',
			actionSubject: 'jiraUiModifications.iframeTimeout',
			attributes: {
				timeoutAfter: `${IFRAME_TIMEOUT_DURATION_MS / 1000}s`,
				extensionId: id,
				viewType,
			},
		});
	}, [forgeExtension, viewType, createAnalyticsEvent]);

	useEffect(() => {
		callbackRef.current = onTimeout;
	}, [onTimeout]);

	const setLoadingComplete = useCallback((): void => {
		clearTimeout(timeoutRef.current);
	}, []);

	useEffect(() => {
		// For details on why we need to evaluate the callback in this way, see
		// https://www.joshwcomeau.com/snippets/react-hooks/use-timeout/#explanation
		// tl;dr: it allows the latest version of `onTimeout` to be called, without
		// needing to cancel and start a new timer each time dependencies change
		const callback = () => callbackRef.current();
		timeoutRef.current = setTimeout(callback, IFRAME_TIMEOUT_DURATION_MS);

		return () => clearTimeout(timeoutRef.current);
	}, []);

	return { setLoadingComplete };
};
